import { format } from "date-fns";
import { wallTime, wallToInstant } from "../../lib/schedule-time/zoned-time";
import type { StreamSlot } from "../../stores/schedule-store";

export type EventReference = { id: string; name: string; color: string };
export type CalendarEvent = StreamSlot & { id: string; start: number; end: number; endTime: string; nextDay: boolean; continued: boolean; lane: number; lanes: number; reference?: EventReference };
type ZonedEvent = { id: string; slot: StreamSlot; start: number; end: number; reference?: EventReference };
const clockTime = (wall: number) => new Date(wall).toISOString().slice(11, 16);

export function convertEvents(slots: Record<string, StreamSlot>, sourceZone: string, displayZone: string, reference?: EventReference): ZonedEvent[] {
  return Object.entries(slots).flatMap(([id, slot]) => {
    if (!slot.active || !/^([01]\d|2[0-3]):[0-5]\d$/.test(slot.time) || !Number.isFinite(slot.duration) || slot.duration <= 0) return [];
    const wall = Date.parse(`${id.slice(0, 10)}T${slot.time}:00Z`);
    if (!Number.isFinite(wall)) return [];
    const instant = wallToInstant(wall, sourceZone);
    const eventId = reference ? `${reference.id}:${id}` : id;
    return [{ id: eventId, slot, reference, start: wallTime(instant, displayZone), end: wallTime(instant + slot.duration * 60000, displayZone) }];
  });
}

export function dayEvents(date: Date, source: ZonedEvent[]): CalendarEvent[] {
  const midnight = Date.parse(`${format(date, "yyyy-MM-dd")}T00:00:00Z`);
  const events: CalendarEvent[] = [];
  for (const { id, slot, start, end, reference } of source) {
    if (start >= midnight + 86400000 || end <= midnight) continue;
    events.push({ ...slot, id, time: clockTime(start), endTime: clockTime(end), nextDay: Math.floor(end / 86400000) > Math.floor(start / 86400000),
      start: Math.max(0, (start - midnight) / 60000), end: Math.min(1440, (end - midnight) / 60000), continued: start < midnight, lane: 0, lanes: 1, reference });
  }
  events.sort((a, b) => a.start - b.start || a.end - b.end);
  // Connected overlap groups share columns; touching boundaries are not overlaps.
  let group: CalendarEvent[] = [];
  let groupEnd = 0;
  const finish = () => {
    const ends: number[] = [];
    for (const event of group) {
      let lane = ends.findIndex(end => end <= event.start);
      if (lane === -1) lane = ends.length;
      ends[lane] = event.end;
      event.lane = lane;
    }
    for (const event of group) event.lanes = ends.length;
  };
  for (const event of events) {
    if (event.start >= groupEnd) { finish(); group = []; }
    group.push(event);
    groupEnd = Math.max(...group.map(item => item.end));
  }
  finish();
  return events;
}
